import React, { useState, useEffect } from 'react';
import { User, Calendar, Phone, Mail, MapPin, Award, Briefcase, Book,Lock , Clock, CreditCard, Heart, Edit, Save, X } from 'lucide-react';
import "../../css/teacher/Profile.css";
import Loader from "../../components/Loader";
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

function Profile() {
  const [teacher, setTeacher] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dob: "",
    gender: "",
    address: "",
    qualification: "",
    experience: "",
    specialization: "",
    joiningDate: "",
    employeeId: "",
    department: "",
    position: "",
    salary: 0,
    bloodGroup: ""
  });

  const [profileImage, setProfileImage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTeacher, setEditedTeacher] = useState({...teacher});
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  // Define sensitive fields that only admin can edit
  const sensitiveFields = [
    "email",
    "employeeId",
    "department",
    "position",
    "joiningDate",
    "salary"
  ];

  // Check if a field is sensitive
  const isSensitiveField = (fieldName) => {
    return sensitiveFields.includes(fieldName);
  };

  // Fetch teacher profile data
  useEffect(() => {
    fetchTeacherProfile();
    fetchProfileImage();
  }, []);

  const fetchTeacherProfile = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Unauthorized! Please login again.");
        return;
      }
      
      const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/profile", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch profile");
      }
      
      const data = await response.json();
      setTeacher(data);
      setEditedTeacher(data);
    } catch (error) {
      console.error("Error fetching teacher profile:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch profile image
  const fetchProfileImage = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/profile/image", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.profileImage) {
          setProfileImage(data.profileImage);
        }
      }
    } catch (error) {
      console.error("Error fetching profile image:", error);
    }
  };

  // Function to handle profile image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (limit to 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size should be less than 2MB");
      return;
    }
    
    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error("Only JPEG, JPG and PNG images are supported");
      return;
    }
    
    setIsUploading(true);
    
    try {
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        try {
          const token = localStorage.getItem("token");
          if (!token) {
            toast.error("Unauthorized! Please login again.");
            setIsUploading(false);
            return;
          }
          
          const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/profile/upload-image", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              imageData: reader.result
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Failed to upload image");
          }
          
          const data = await response.json();
          setProfileImage(data.profileImage);
          toast.success("Profile image updated successfully!");
        } catch (error) {
          console.error("Error uploading image:", error);
          toast.error(`Error: ${error.message}`);
        } finally {
          setIsUploading(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error(`Error: ${error.message}`);
      setIsUploading(false);
    }
  };

  // Function to handle profile update
  const saveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Unauthorized! Please login again.");
        return;
      }

      // Filter out sensitive fields from the update payload
      const updatedData = {};
      for (const key in editedTeacher) {
        if (!isSensitiveField(key)) {
          updatedData[key] = editedTeacher[key];
        } else {
          // Keep the original values for sensitive fields
          updatedData[key] = teacher[key];
        }
      }

      const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/profile", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(updatedData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update profile");
      }

      const updatedProfile = await response.json();
      setTeacher(updatedProfile);
      setIsEditing(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // Function to toggle edit mode
  const toggleEditMode = () => {
    if (isEditing) {
      // Save the changes
      saveProfile();
    } else {
      // Enter edit mode
      setEditedTeacher({...teacher});
      setIsEditing(true);
    }
  };

  // Function to handle field changes in edit mode
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Don't update if it's a sensitive field (double protection)
    if (isSensitiveField(name)) {
      return;
    }
    
    setEditedTeacher({ ...editedTeacher, [name]: value });
  };

  // Function to cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    setEditedTeacher({...teacher});
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Render field based on whether it's sensitive or not
  const renderField = (name, label, type = "text", options = null) => {
    const isDateField = type === "date";
    const isSelectField = type === "select";
    
    return (
      <div className="teacherprofile-field">
        <label>{label}</label>
        {isEditing ? (
          isSensitiveField(name) ? (
            // If sensitive field, show as read-only during edit
            <div className="teacherprofile-sensitive-field">
              {isDateField 
                ? formatDate(teacher[name]) 
                : name === "salary" 
                  ? `₹${teacher[name] ? teacher[name].toLocaleString() : 0}`
                  : teacher[name]}
              <span className="teacherprofile-admin-only">(Admin only)</span>
            </div>
          ) : (
            // Regular editable field
            isSelectField ? (
              <select 
                name={name} 
                value={editedTeacher[name]} 
                onChange={handleChange}
              >
                {options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : isDateField ? (
              <input 
                type="date" 
                name={name} 
                value={editedTeacher[name] ? new Date(editedTeacher[name]).toISOString().split('T')[0] : ''} 
                onChange={handleChange} 
              />
            ) : type === "number" ? (
              <input 
                type="number" 
                name={name} 
                value={editedTeacher[name]} 
                onChange={handleChange} 
              />
            ) : type === "textarea" ? (
              <textarea 
                name={name} 
                value={editedTeacher[name]} 
                onChange={handleChange}
                rows="3"
              ></textarea>
            ) : (
              <input 
                type={type} 
                name={name} 
                value={editedTeacher[name]} 
                onChange={handleChange} 
              />
            )
          )
        ) : (
          // Display mode (not editing)
          <div>
            {isDateField 
              ? formatDate(teacher[name]) 
              : name === "salary" 
                ? `₹${teacher[name] ? teacher[name].toLocaleString() : 0}`
                : name === "gender" 
                  ? teacher[name] ? teacher[name].charAt(0).toUpperCase() + teacher[name].slice(1) : ""
                  : teacher[name]}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="teacherprofile-container">
      <div className="teacherprofile-header">
        <div className="teacherprofile-header-content">
          <h1>Teacher Profile</h1>
          <div className="teacherprofile-actions">
            {isEditing ? (
              <>
                <button className="teacherprofile-button save" onClick={toggleEditMode}>
                  <Save size={18} />
                  Save Changes
                </button>
                <button className="teacherprofile-button cancel" onClick={cancelEditing}>
                  <X size={18} />
                  Cancel
                </button>
              </>
            ) : (
              <button className="teacherprofile-button edit" onClick={toggleEditMode}>
                <Edit size={18} />
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="teacherprofile-content">
        <div className="teacherprofile-sidebar">
          <div className="teacherprofile-image-container">
            {isUploading ? (
              <div className="teacherprofile-image-loading">
                <Loader />
              </div>
            ) : profileImage ? (
              <img 
                src={profileImage} 
                alt="Profile" 
                className="teacherprofile-image" 
              />
            ) : (
              <div className="teacherprofile-image-placeholder">
                <User size={64} />
              </div>
            )}
            <div className="teacherprofile-upload-container">
              <label htmlFor="profile-upload" className="teacherprofile-upload-button">
                {isUploading ? "Uploading..." : "Change Photo"}
              </label>
              <input 
                id="profile-upload" 
                type="file" 
                accept="image/jpeg,image/jpg,image/png" 
                onChange={handleImageUpload} 
                className="teacherprofile-file-input" 
                disabled={isUploading}
              />
            </div>
          </div>

          <div className="teacherprofile-info-card">
            <h2 className="teacherprofile-name">
              {teacher.firstName} {teacher.lastName}
            </h2>
            <div className="teacherprofile-position">{teacher.position}</div>
            <div className="teacherprofile-department">{teacher.department}</div>
            <div className="teacherprofile-id">ID: {teacher.employeeId}</div>
          </div>

          <div className="teacherprofile-contact-info">
            <h3>Contact Information</h3>
            <div className="teacherprofile-info-item">
              <Mail className="teacherprofile-icon" size={16} />
              <span>{teacher.email}</span>
            </div>
            <div className="teacherprofile-info-item">
              <Phone className="teacherprofile-icon" size={16} />
              <span>{teacher.phone}</span>
            </div>
            <div className="teacherprofile-info-item">
              <MapPin className="teacherprofile-icon" size={16} />
              <span>{teacher.address}</span>
            </div>
          </div>
          <div className="teacherprofile-contact-info">
            <h3>Privacy Information</h3>
            
            <div className="teacherprofile-info-item">
              <Lock className="teacherprofile-icon" size={16} />
              <Link to={'/teacher/changePassword'}><b><u>Change Password</u></b></Link>
            </div>
          </div>
        </div>

        <div className="teacherprofile-main">
          <div className="teacherprofile-section">
            <h3>
              <User className="teacherprofile-section-icon" size={18} />
              Personal Information
            </h3>
            <div className="teacherprofile-grid">
              {renderField("firstName", "First Name")}
              {renderField("lastName", "Last Name")}
              {renderField("dob", "Date of Birth", "date")}
              {renderField("gender", "Gender", "select", [
                { value: "male", label: "Male" },
                { value: "female", label: "Female" },
                { value: "other", label: "Other" }
              ])}
              {renderField("bloodGroup", "Blood Group", "select", [
                { value: "A+", label: "A+" },
                { value: "A-", label: "A-" },
                { value: "B+", label: "B+" },
                { value: "B-", label: "B-" },
                { value: "AB+", label: "AB+" },
                { value: "AB-", label: "AB-" },
                { value: "O+", label: "O+" },
                { value: "O-", label: "O-" }
              ])}
              {renderField("email", "Email")}
              {renderField("phone", "Phone Number")}
            </div>
          </div>

          <div className="teacherprofile-section">
            <h3>
              <Award className="teacherprofile-section-icon" size={18} />
              Professional Information
            </h3>
            <div className="teacherprofile-grid">
              {renderField("department", "Department")}
              {renderField("position", "Position")}
              {renderField("employeeId", "Employee ID")}
              {renderField("joiningDate", "Joining Date", "date")}
              {renderField("experience", "Experience")}
              {renderField("salary", "Salary", "number")}
            </div>
          </div>

          <div className="teacherprofile-section">
            <h3>
              <Book className="teacherprofile-section-icon" size={18} />
              Academic Information
            </h3>
            <div className="teacherprofile-grid">
              {renderField("qualification", "Qualification")}
              {renderField("specialization", "Specialization")}
            </div>
          </div>

          <div className="teacherprofile-section">
            <h3>
              <MapPin className="teacherprofile-section-icon" size={18} />
              Address
            </h3>
            <div className="teacherprofile-grid">
              <div className="teacherprofile-field full-width">
                <label>Address</label>
                {isEditing ? (
                  <textarea 
                    name="address" 
                    value={editedTeacher.address} 
                    onChange={handleChange}
                    rows="3"
                  ></textarea>
                ) : (
                  <div>{teacher.address}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;